import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        // Use configurable API version to build baseURL
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })
        
        // Add request interceptor to add auth token (optional for messages)
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

interface Message {
    id: string
    name: string
    email: string
    phoneNumber?: string
    subject: string
    message: string
    created_at: string
}

interface MessageCreateData {
    name: string
    email: string
    phoneNumber?: string
    subject: string
    message: string
}

export const messageService = {
    getMessages,
    createMessage,
    deleteMessage,
}

/**
 * Get all messages
 */
async function getMessages(): Promise<ApiResponse<Message[]>> {
    try {
        const response = await getApiInstance().get('/messages')
        
        return {
            success: true,
            data: response.data
        }
    } catch (error: any) {
        console.error('Error fetching messages:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch messages'
        }
    }
}

/**
 * Create a new message (no auth required)
 */
async function createMessage(data: MessageCreateData): Promise<ApiResponse<Message>> {
    try {
        // Create instance without auth for public message creation
        const config = getConfig()
        const response = await axios.post(
            `${config.apiUrl}/messages`,
            data,
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
        
        return {
            success: true,
            data: response.data
        }
    } catch (error: any) {
        console.error('Error creating message:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to send message'
        }
    }
}

/**
 * Delete a message
 */
async function deleteMessage(id: string): Promise<ApiResponse<void>> {
    try {
        await getApiInstance().delete(`/messages/${id}`)
        
        return {
            success: true,
            data: undefined
        }
    } catch (error: any) {
        console.error('Error deleting message:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to delete message'
        }
    }
}

// Export types
export type { Message, MessageCreateData }
